import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import './style.scss'
import Controls from './controls/controls'
import Main from 'layout/main'
import useControls from '../../hooks/controls'
import { useLocation, useParams } from 'react-router-dom'
import GroupWorkoutHeader from 'pages/group-workout/components/header'
import { useGroupWorkoutByIdQuery } from 'api/queries/group-workouts'
import { useCurrentGroupWorkout } from 'store/group-workout/selectors'
import { useGroupWorkoutSessionByIdQuery } from 'api/queries/group-workout-session'
import HitSimulate from 'components/hit-simulate/hit-simulate'
import useAddGroupHit from 'lib/hooks/useAddGroupHit'
import useSubscriptionHeartbeat from 'lib/hooks/useSubscriptionHeartbeat'
import {
  useCurrentGroupWorkoutSession,
  useLiveGroupWorkoutHits,
  useMissedGroupWorkoutHitSyncVersion,
  useGroupWorkoutSessionActiveTrack,
  useGroupWorkoutSessionActiveRound,
  useGroupWorkoutSessionActions,
} from 'store/group-workout-session/selectors'
import type { GroupWorkoutHitRoundTarget } from 'store/group-workout-session'
import GroupIntensity from 'components/group-workout/group-intensity'
import GroupHitChallenge from 'components/group-workout/group-hit-challenge'
import GroupGroupChallenge from 'components/group-workout/group-group-challenge'
import CircularTimer from 'components/group-workout/circular-timer'
import { SERVER_TIME } from 'api/queries/server-time'
import initApollo, { restartApiSubscriptions } from 'api/client'
// import { useSaveGroupWorkoutSession } from 'api/mutations/group-workout-session'
import GroupPause from 'components/popup/group-pause/group-pause'
import ControlsFooter from './controls/controls-footer'
import GroupBag from 'components/popup/group-bag/group-bag'
import GroupSwitch from 'components/popup/group-switch/group-switch'
import GroupQuote from 'components/group-workout/group-quote'
import NewTrackList from './header/new-track-list'
import { mergeGroupWorkoutHits } from 'lib/group-workout-hits'
import { APP_VERSION } from 'lib/app-version'
import useGroupWorkoutCountdownCue from 'lib/hooks/useGroupWorkoutCountdownCue'
import {
  GroupBag as GroupBagModel,
  GroupWorkoutHit,
  GroupWorkoutSession,
  GroupWorkoutTrack,
  GroupWorkoutTrackRound,
} from 'interfaces/group-workout'

const SERVER_TIME_SYNC_INTERVAL_MS = 10000
const TIMER_UPDATE_THRESHOLD_SECONDS = 0.05
const SMOOTH_TIMER_FRAME_INTERVAL_MS = 100
const GROUP_SUBSCRIPTION_HEARTBEAT_STALL_MS = 5_000
const GROUP_SUBSCRIPTION_HEARTBEAT_CHECK_MS = 1_000
const EMPTY_GROUP_BAGS: GroupBagModel[] = []
const EMPTY_GROUP_HITS: GroupWorkoutHit[] = []
const EMPTY_SESSION_TRACKS: GroupWorkoutSession['groupWorkoutSessionTracks'] = []
const EMPTY_WORKOUT_TRACKS: GroupWorkoutTrack[] = []
// Versioned so a replaced sound is fetched instead of a browser-cached copy.
const GROUP_WORKOUT_COUNTDOWN_AUDIO = `/group-workout-countdown.wav?v=${APP_VERSION}`

const toServerTimeMs = (serverTime: unknown): number | null => {
  const parsedTime = typeof serverTime === 'number'
    ? serverTime
    : new Date(String(serverTime)).getTime()

  return Number.isFinite(parsedTime) ? parsedTime : null
}

const updateTimeIfChanged = (
  setTime: React.Dispatch<React.SetStateAction<number>>,
  nextTime: number,
) => {
  setTime((currentTime) =>
    Math.abs(currentTime - nextTime) >= TIMER_UPDATE_THRESHOLD_SECONDS ? nextTime : currentTime
  )
}

const clampArrayIndex = (index: number, length: number) => {
  if (!Number.isFinite(index) || length <= 0) return 0

  return Math.max(0, Math.min(Math.floor(index), length - 1))
}

const GroupWorkoutHitSubscription = ({ target }: { target: GroupWorkoutHitRoundTarget }) => {
  useAddGroupHit(target)
  return null
}

const GroupWorkout: React.FC = () => {
  const location = useLocation()
  const { id: routeId } = useParams()
  const activeTrack: number = useGroupWorkoutSessionActiveTrack()
  const activeRound: number = useGroupWorkoutSessionActiveRound()

  const { updateActivePosition, clearLiveGroupWorkoutHits } = useGroupWorkoutSessionActions()
  const locationState = location.state as { id?: string } | null
  const id = locationState?.id ?? routeId ?? ''
  const groupWorkoutSession = useCurrentGroupWorkoutSession()
  // useEffect(() => {
  //   console.log('groupWorkoutSession updated:', groupWorkoutSession)
  // }, [groupWorkoutSession])

  //const { saveGroupWorkoutSession } = useSaveGroupWorkoutSession()
  //console.log(groupWorkoutSession);
  useGroupWorkoutSessionByIdQuery(id)
  const storedGroupWorkout = useCurrentGroupWorkout()
  const sessionGroupWorkout = groupWorkoutSession?.groupWorkout
  const groupWorkout = storedGroupWorkout ?? (
    sessionGroupWorkout?.groupWorkoutTracks?.length ? sessionGroupWorkout : null
  )
  const liveGroupWorkoutHits = useLiveGroupWorkoutHits()
  useGroupWorkoutByIdQuery(groupWorkoutSession?.groupWorkout?.id ?? '', {
    skip: !!groupWorkout && !!groupWorkoutSession && groupWorkout.id === groupWorkoutSession?.groupWorkout?.id,
  })

  const [showPausePopup, toggleShowPausePopup] = useState<boolean>(false)
  const [showBagPopup, toggleShowBagPopup] = useState<boolean>(false)
  const [showSwitchPopupExcercise, toggleShowSwitchPopupExcercise] = useState<boolean>(false)
  const [showSwitchPopupEncourage, toggleShowSwitchPopupEncourage] = useState<boolean>(false)

  const sessionTracks = groupWorkoutSession?.groupWorkoutSessionTracks ?? EMPTY_SESSION_TRACKS
  const workoutTracks = groupWorkout?.groupWorkoutTracks ?? EMPTY_WORKOUT_TRACKS
  const workoutTrack = workoutTracks[activeTrack]
  const sessionTrack = useMemo(() => {
    if (!workoutTrack) return sessionTracks[activeTrack]

    return sessionTracks.find((track) => track.trackId === workoutTrack.id) ?? sessionTracks[activeTrack]
  }, [activeTrack, sessionTracks, workoutTrack])
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const countdownAudioRef = useRef<HTMLAudioElement | null>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [showVolumeSlider, setShowVolumeSlider] = useState(false)
  const [volume, setVolume] = useState(1)  // volume value between 0 and 1
  const toggleVolumeSlider = useCallback(() => setShowVolumeSlider(prev => !prev), [])
  const playMedia = useCallback((media: HTMLMediaElement | null) => {
    const playResult = media?.play()
    playResult?.catch((error) => {
      if (error?.name === 'AbortError') return
      console.error('Failed to play media:', error)
    })
  }, [])

  const workoutRound = useMemo<GroupWorkoutTrackRound | null>(() => {
    if (workoutTrack?.groupWorkoutTrackRounds && activeRound < workoutTrack.groupWorkoutTrackRounds.length) {
      return workoutTrack.groupWorkoutTrackRounds[activeRound]
    }

    return null
  }, [workoutTrack, activeRound])
  const sessionRound = useMemo(() => {
    if (!sessionTrack) return undefined
    if (!workoutRound) return sessionTrack.groupWorkoutSessionRounds?.[activeRound]

    return (
      sessionTrack.groupWorkoutSessionRounds?.find((round) => round.roundId === workoutRound.id) ??
      sessionTrack.groupWorkoutSessionRounds?.[activeRound]
    )
  }, [activeRound, sessionTrack, workoutRound])

  useLayoutEffect(() => {
    if (!groupWorkoutSession || workoutTracks.length === 0) return
    if (workoutTrack && workoutRound) return

    const nextActiveTrack = clampArrayIndex(activeTrack, workoutTracks.length)
    const nextWorkoutTrack = workoutTracks[nextActiveTrack]
    const nextActiveRound = clampArrayIndex(
      activeRound,
      nextWorkoutTrack?.groupWorkoutTrackRounds?.length ?? 0,
    )

    if (nextActiveTrack !== activeTrack || nextActiveRound !== activeRound) {
      updateActivePosition(nextActiveTrack, nextActiveRound)
    }
  }, [
    activeRound,
    activeTrack,
    groupWorkoutSession,
    updateActivePosition,
    workoutRound,
    workoutTrack,
    workoutTracks,
    workoutTracks.length,
  ])
  const workoutRoundId = workoutRound?.id ?? null
  const groupBags = groupWorkoutSession?.groupBags ?? EMPTY_GROUP_BAGS
  const currentRoundHits = sessionRound?.groupWorkoutHits ?? EMPTY_GROUP_HITS
  const currentLiveRoundHits = useMemo(
    () => mergeGroupWorkoutHits(currentRoundHits, liveGroupWorkoutHits),
    [currentRoundHits, liveGroupWorkoutHits]
  )
  const currentHitRoundTarget = useMemo<GroupWorkoutHitRoundTarget>(
    () => ({ sessionRoundId: sessionRound?.id ?? null }),
    [sessionRound?.id]
  )
  const liveHitRoundScopeRef = useRef<string | null>(null)
  const [hitSubscriptionRestartKey, setHitSubscriptionRestartKey] = useState(0)
  const missedGroupWorkoutHitSyncVersion = useMissedGroupWorkoutHitSyncVersion()
  const { lastHeartbeatAt } = useSubscriptionHeartbeat()
  const lastHeartbeatAtRef = useRef(lastHeartbeatAt)
  const handledMissedHitSyncVersionRef = useRef(missedGroupWorkoutHitSyncVersion)

  const [roundTime, setRoundTime] = useState<number>(10)
  // Handles switch logic.
  const [currentSwitchIndex, setCurrentSwitchIndex] = useState<number>(0)
  const [switchTimeLeft, setSwitchTimeLeft] = useState<number>(0)
  const switchInfo = workoutRound?.isSwitchRound
    ? {
        currentSwitch: currentSwitchIndex + 1,
        totalSwitches: workoutRound.totalSwitchRounds,
      }
    : null
  const usesLiveHits = !!workoutRound && (
    workoutRound.showIntensity ||
    workoutRound.showHitScore ||
    workoutRound.showGroupChallenge
  )

  const client = useMemo(() => initApollo(), [])
  const [serverTime, setServerTime] = useState<number | null>(null)

  const getUpdatedServerTime = useCallback(async () => {
    try {
      const { data } = await client.query({
        query: SERVER_TIME,
        fetchPolicy: 'no-cache',
      })
      return toServerTimeMs(data.getServerTime.serverTime)
    } catch (error) {
      console.error('Error fetching server time:', error)
      return null
    }
  }, [client])

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume
    }
  }, [volume])

  useEffect(() => {
    lastHeartbeatAtRef.current = lastHeartbeatAt
  }, [lastHeartbeatAt])

  useEffect(() => {
    if (!usesLiveHits) return undefined

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return

      const timeSinceLastHeartbeat = Date.now() - lastHeartbeatAtRef.current
      if (timeSinceLastHeartbeat >= GROUP_SUBSCRIPTION_HEARTBEAT_STALL_MS) {
        lastHeartbeatAtRef.current = Date.now()
        restartApiSubscriptions()
        setHitSubscriptionRestartKey((restartKey) => restartKey + 1)
      }
    }, GROUP_SUBSCRIPTION_HEARTBEAT_CHECK_MS)

    return () => window.clearInterval(intervalId)
  }, [usesLiveHits])

  useEffect(() => {
    if (!usesLiveHits) {
      handledMissedHitSyncVersionRef.current = missedGroupWorkoutHitSyncVersion
      return
    }

    if (
      missedGroupWorkoutHitSyncVersion === 0 ||
      handledMissedHitSyncVersionRef.current === missedGroupWorkoutHitSyncVersion
    ) {
      return
    }

    handledMissedHitSyncVersionRef.current = missedGroupWorkoutHitSyncVersion
    console.warn('Group workout hit subscription missed persisted hits; remounting hit subscription.')
    setHitSubscriptionRestartKey((restartKey) => restartKey + 1)
  }, [missedGroupWorkoutHitSyncVersion, usesLiveHits])

  useEffect(() => {
    let isMounted = true

    const fetchServerTime = async () => {
      const time = await getUpdatedServerTime()
      if (isMounted && time !== null) {
        setServerTime(time)
      }
    }

    fetchServerTime()
    const intervalId = setInterval(fetchServerTime, SERVER_TIME_SYNC_INTERVAL_MS)

    return () => {
      isMounted = false
      clearInterval(intervalId)
    }
  }, [getUpdatedServerTime])

  useLayoutEffect(() => {
    if (workoutRound?.isSwitchRound && workoutRound?.timeInSwitchRound) {
      setSwitchTimeLeft(workoutRound.timeInSwitchRound) // Reset switch time
      setCurrentSwitchIndex(0) // Start from the first switch
    }
  }, [workoutRound?.id, workoutRound?.isSwitchRound, workoutRound?.timeInSwitchRound])

  useLayoutEffect(() => {
    if (workoutRound?.duration) {
      setRoundTime(workoutRound.duration)
    }
  }, [workoutRound?.id, workoutRound?.duration])

  useLayoutEffect(() => {
    if (!groupWorkoutSession?.id || !workoutRoundId) return

    const liveHitRoundScope = `${groupWorkoutSession.id}:${activeTrack}:${activeRound}:${workoutRoundId}`
    if (liveHitRoundScopeRef.current === null) {
      liveHitRoundScopeRef.current = liveHitRoundScope
      return
    }

    if (liveHitRoundScopeRef.current !== liveHitRoundScope) {
      liveHitRoundScopeRef.current = liveHitRoundScope
      clearLiveGroupWorkoutHits()
    }
  }, [activeRound, activeTrack, clearLiveGroupWorkoutHits, groupWorkoutSession?.id, workoutRoundId])

  const { isPaused, handlePause } = useControls()
  const countdownTimeLeft = workoutRound?.isSwitchRound ? switchTimeLeft : roundTime
  const countdownKey = workoutRoundId
    ? workoutRound?.isSwitchRound
      ? `${workoutRoundId}:switch:${currentSwitchIndex}`
      : `${workoutRoundId}:round`
    : null

  useGroupWorkoutCountdownCue({
    audioRef: countdownAudioRef,
    countdownKey,
    isPaused,
    playMedia,
    timeLeft: countdownTimeLeft,
    volume,
  })

  const customHandlePause = () => {
    toggleShowPausePopup(true)
    handlePause()
    if (isPaused) {
      playMedia(audioRef.current)
      playMedia(videoRef.current)
    } else {
      audioRef.current?.pause()
      videoRef.current?.pause()
    }
  }

  const audioSource = groupWorkout?.audioFile?.blobUrl ?? null
  const latestWorkoutStateRef = useRef<{
    activeRound: number
    activeTrack: number
    currentSwitchIndex: number
    groupWorkoutSession: GroupWorkoutSession | null
    workoutRound: GroupWorkoutTrackRound | null
    workoutTrack: GroupWorkoutTrack | undefined
    workoutTracks: GroupWorkoutTrack[]
  }>({
    activeRound: 0,
    activeTrack: 0,
    currentSwitchIndex: 0,
    groupWorkoutSession: null,
    workoutRound: null,
    workoutTrack: undefined,
    workoutTracks: [],
  })
  latestWorkoutStateRef.current = {
    activeRound,
    activeTrack,
    currentSwitchIndex,
    groupWorkoutSession,
    workoutRound,
    workoutTrack,
    workoutTracks,
  }

  const roundCompletionKeyRef = useRef<string | null>(null)
  const switchCompletionKeyRef = useRef<string | null>(null)
  const pendingAudioSeekRef = useRef<{ roundId: string; time: number } | null>(null)
  const switchPopupTimeoutsRef = useRef<{
    exercise: ReturnType<typeof setTimeout> | null
    encourage: ReturnType<typeof setTimeout> | null
  }>({ exercise: null, encourage: null })
  const switchPopupKeysRef = useRef<{ exercise: string | null; encourage: string | null }>({
    exercise: null,
    encourage: null,
  })

  const scheduleSwitchPopup = useCallback((kind: 'exercise' | 'encourage', key: string) => {
    if (switchPopupKeysRef.current[kind] === key) return

    switchPopupKeysRef.current[kind] = key
    if (switchPopupTimeoutsRef.current[kind]) {
      clearTimeout(switchPopupTimeoutsRef.current[kind]!)
    }

    const setVisible = kind === 'exercise' ? toggleShowSwitchPopupExcercise : toggleShowSwitchPopupEncourage
    setVisible(true)
    switchPopupTimeoutsRef.current[kind] = setTimeout(() => {
      setVisible(false)
      switchPopupTimeoutsRef.current[kind] = null
    }, 3000)
  }, [])

  useEffect(() => {
    const switchPopupTimeouts = switchPopupTimeoutsRef.current

    return () => {
      if (switchPopupTimeouts.exercise) {
        clearTimeout(switchPopupTimeouts.exercise)
      }
      if (switchPopupTimeouts.encourage) {
        clearTimeout(switchPopupTimeouts.encourage)
      }
    }
  }, [])

  const seekAudioToRound = useCallback((roundId: string, time: number) => {
    if (!Number.isFinite(time)) return

    try {
      if (audioRef.current) {
        audioRef.current.currentTime = time
      }
    } catch (error) {
      console.warn(`Could not seek group workout audio for round ${roundId}.`, error)
    }
  }, [])

  useLayoutEffect(() => {
    const pendingSeek = pendingAudioSeekRef.current
    if (!pendingSeek || pendingSeek.roundId !== workoutRoundId) return

    pendingAudioSeekRef.current = null
    seekAudioToRound(pendingSeek.roundId, pendingSeek.time)
  }, [seekAudioToRound, workoutRoundId])

  const moveToWorkoutPosition = useCallback((
    nextActiveTrack: number,
    nextActiveRound: number,
    options: { seekAudio?: boolean } = {},
  ) => {
    const nextTrack = latestWorkoutStateRef.current.workoutTracks[nextActiveTrack]
    const nextRound = nextTrack?.groupWorkoutTrackRounds?.[nextActiveRound]
    if (!nextRound) return

    roundCompletionKeyRef.current = null
    switchCompletionKeyRef.current = null
    pendingAudioSeekRef.current = options.seekAudio
      ? { roundId: nextRound.id, time: nextRound.calculatedAudioTime }
      : null

    updateActivePosition(nextActiveTrack, nextActiveRound)
  }, [updateActivePosition])

  const handleRoundComplete = useCallback(() => {
    const {
      activeRound: currentActiveRound,
      activeTrack: currentActiveTrack,
      groupWorkoutSession: currentSession,
      workoutTrack: currentWorkoutTrack,
      workoutTracks: currentWorkoutTracks,
    } = latestWorkoutStateRef.current

    if (!currentWorkoutTrack || !currentSession) return

    const totalRounds = currentWorkoutTrack.groupWorkoutTrackRounds.length ?? 0
    if (totalRounds === 0) return

    const isLastRound = currentActiveRound >= totalRounds - 1
    const isLastTrack = currentActiveTrack >= currentWorkoutTracks.length - 1
    if (isLastRound) {
      if (!isLastTrack) {
        moveToWorkoutPosition(currentActiveTrack + 1, 0)
      }
      return
    }

    moveToWorkoutPosition(currentActiveTrack, currentActiveRound + 1)
  }, [moveToWorkoutPosition])

  const handleSwitchComplete = useCallback(() => {
    const { currentSwitchIndex: switchIndex, workoutRound: currentRound } = latestWorkoutStateRef.current
    if (!currentRound) return

    const isLastSwitch = switchIndex >= currentRound.totalSwitchRounds - 1
    if (isLastSwitch) {
      setSwitchTimeLeft(0)
      return
    }

    setCurrentSwitchIndex(switchIndex + 1)
    setSwitchTimeLeft(currentRound.timeInSwitchRound)
  }, [])

  // Handle synchronization between timer and audio playback
  useEffect(() => {
    const audioElement = audioRef.current

    if (!audioElement || !audioSource || !workoutRoundId) return

    roundCompletionKeyRef.current = null
    switchCompletionKeyRef.current = null

    const syncTimersWithAudio = () => {
      const currentRound = latestWorkoutStateRef.current.workoutRound
      if (!currentRound) return

      const roundStartTime = currentRound.calculatedAudioTime
      const roundElapsed = Math.max(0, audioElement.currentTime - roundStartTime)
      const timeLeft = Math.max(currentRound.duration - roundElapsed, 0)
      updateTimeIfChanged(setRoundTime, timeLeft)

      if (currentRound.isSwitchRound) {
        const switchIndex = latestWorkoutStateRef.current.currentSwitchIndex
        const switchStartTime = currentRound.calculatedAudioTime + switchIndex * currentRound.timeInSwitchRound
        const switchElapsed = Math.max(0, audioElement.currentTime - switchStartTime)
        const switchTimeLeft = Math.max(currentRound.timeInSwitchRound - switchElapsed, 0)
        updateTimeIfChanged(setSwitchTimeLeft, switchTimeLeft)

        const isLastSwitch = switchIndex >= currentRound.totalSwitchRounds - 1
        const switchKey = `${currentRound.id}:${switchIndex}`
        if (!isLastSwitch && switchTimeLeft < 1.5 && currentRound.showSwitchScreenEncourage) {
          scheduleSwitchPopup('encourage', switchKey)
        }
        if (!isLastSwitch && switchTimeLeft < 1.5 && currentRound.showSwitchScreenExercise) {
          scheduleSwitchPopup('exercise', switchKey)
        }

        if (switchTimeLeft <= TIMER_UPDATE_THRESHOLD_SECONDS) {
          if (switchCompletionKeyRef.current !== switchKey) {
            switchCompletionKeyRef.current = switchKey
            handleSwitchComplete()
          }
        }
      }

      if (timeLeft <= TIMER_UPDATE_THRESHOLD_SECONDS) {
        const completionKey = `${currentRound.id}:${latestWorkoutStateRef.current.activeTrack}:${latestWorkoutStateRef.current.activeRound}`
        if (roundCompletionKeyRef.current === completionKey) return
        roundCompletionKeyRef.current = completionKey
        handleRoundComplete()
      }
    }

    let animationFrameId: number | null = null
    let lastAnimationSyncMs = 0

    const stopSmoothTimer = () => {
      if (animationFrameId === null) return
      window.cancelAnimationFrame(animationFrameId)
      animationFrameId = null
    }

    const runSmoothTimerFrame = (timestamp: number) => {
      if (timestamp - lastAnimationSyncMs >= SMOOTH_TIMER_FRAME_INTERVAL_MS) {
        lastAnimationSyncMs = timestamp
        syncTimersWithAudio()
      }

      animationFrameId = window.requestAnimationFrame(runSmoothTimerFrame)
    }

    const startSmoothTimer = () => {
      if (animationFrameId !== null) return
      animationFrameId = window.requestAnimationFrame(runSmoothTimerFrame)
    }

    const syncAndStopSmoothTimer = () => {
      syncTimersWithAudio()
      stopSmoothTimer()
    }

    audioElement.addEventListener('timeupdate', syncTimersWithAudio)
    audioElement.addEventListener('seeked', syncTimersWithAudio)
    audioElement.addEventListener('play', startSmoothTimer)
    audioElement.addEventListener('playing', startSmoothTimer)
    audioElement.addEventListener('pause', syncAndStopSmoothTimer)
    audioElement.addEventListener('ended', syncAndStopSmoothTimer)

    syncTimersWithAudio()
    if (!audioElement.paused && !audioElement.ended) {
      startSmoothTimer()
    }

    return () => {
      audioElement.removeEventListener('timeupdate', syncTimersWithAudio)
      audioElement.removeEventListener('seeked', syncTimersWithAudio)
      audioElement.removeEventListener('play', startSmoothTimer)
      audioElement.removeEventListener('playing', startSmoothTimer)
      audioElement.removeEventListener('pause', syncAndStopSmoothTimer)
      audioElement.removeEventListener('ended', syncAndStopSmoothTimer)
      stopSmoothTimer()
    }
  }, [audioSource, handleRoundComplete, handleSwitchComplete, scheduleSwitchPopup, workoutRoundId])

  // Handle next and previous round transitions
  const customHandleNext = () => {
    if (!workoutTrack) return

    const isLastRound = activeRound >= workoutTrack.groupWorkoutTrackRounds.length - 1
    if (isLastRound) {
      const isLastTrack = activeTrack >= workoutTracks.length - 1
      if (!isLastTrack) {
        moveToWorkoutPosition(activeTrack + 1, 0, { seekAudio: true })
      }
    } else {
      moveToWorkoutPosition(activeTrack, activeRound + 1, { seekAudio: true })
    }
  }

  const customHandlePrev = () => {
    if (!workoutTrack) return

    if (activeRound > 0) {
      moveToWorkoutPosition(activeTrack, activeRound - 1, { seekAudio: true })
    } else if (activeTrack > 0) {
      const previousTrack = workoutTracks[activeTrack - 1]
      const lastRoundIndex = previousTrack.groupWorkoutTrackRounds.length - 1
      moveToWorkoutPosition(activeTrack - 1, lastRoundIndex, { seekAudio: true })
    }
  }

  // Play Audio on Component Load
  useEffect(() => {
    if (audioRef.current && audioSource) {
      playMedia(audioRef.current)
    }
  }, [audioSource, playMedia])

  const closeBagPopup = () => {
    toggleShowBagPopup(false)
    handlePause()
    if (isPaused) {
      playMedia(audioRef.current)
      playMedia(videoRef.current)
    } else {
      audioRef.current?.pause()
      videoRef.current?.pause()
    }
  }

  const resumeWorkout = () => {
    toggleShowPausePopup(false)
    handlePause()
    if (isPaused) {
      playMedia(audioRef.current)
      playMedia(videoRef.current)
    } else {
      audioRef.current?.pause()
      videoRef.current?.pause()
    }
  }

  const openBags = () => {
    toggleShowBagPopup(true)
    handlePause()
    if (isPaused) {
      playMedia(audioRef.current)
      playMedia(videoRef.current)
    } else {
      audioRef.current?.pause()
      videoRef.current?.pause()
    }
  }

  const formatTime = (): string => {
    let seconds = 0
    if (groupWorkout && workoutRound) {
      seconds = Math.max(0, groupWorkout.duration - workoutRound.calculatedAudioTime - workoutRound.duration + roundTime)
    }
    const hrs = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)

    return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
  }

  const getCurrentExerciseSecond = () => {
    if (!workoutRound) return 0
    const elapsed = workoutRound.isSwitchRound
      ? (workoutRound.timeInSwitchRound ?? 0) - switchTimeLeft
      : workoutRound.duration - roundTime

    return Math.max(0, elapsed)
  }

  return (
    <Main>
      <div
        className="workout-new"
        data-testid="group-workout-screen"
        data-active-track={activeTrack}
        data-active-round={activeRound}
        data-active-round-id={workoutRound?.id ?? ''}
        data-active-round-name={workoutRound?.name ?? ''}
        data-session-workout-id={groupWorkoutSession?.groupWorkout?.id ?? ''}
        data-session-track-count={sessionTracks.length}
        data-workout-id={groupWorkout?.id ?? ''}
        data-workout-track-count={workoutTracks.length}
      >
        <GroupWorkoutHitSubscription key={hitSubscriptionRestartKey} target={currentHitRoundTarget} />
        {showBagPopup && <GroupBag resumeWorkout={() => {}} closePopup={() => closeBagPopup()} />}
        {showSwitchPopupExcercise && <GroupSwitch />}
        {showSwitchPopupEncourage && <GroupSwitch isEncouragement={true}/>}
        {showPausePopup && <GroupPause resumeWorkout={() => resumeWorkout()} closePopup={() => resumeWorkout()} />}

        <div className="group-header">
          <GroupWorkoutHeader>
            <div
              style={{
                color: 'white',
                display: 'flex',
                flexDirection: 'column',
                minWidth: '280px',
                textAlign: 'left',
                margin: '20px 20px 20px 50px',
                alignItems: 'start',
              }}
            >
              <div style={{ fontSize: '1.5rem', fontFamily: 'CanaroBold' }}>CURRENT SESSION</div>
              <div
                style={{ fontSize: '3.2rem', fontFamily: 'CanaroBold' }}
                data-testid="group-workout-timer"
              >
                {groupWorkout?.duration ? formatTime() : '00:00:00'}
              </div>
            </div>
            {groupWorkout && workoutRound &&
              <NewTrackList
                groupWorkout={groupWorkout}
                activeRoundId={workoutRound.id}
                isPaused={isPaused}
                roundTime={roundTime}
              />
            }
          </GroupWorkoutHeader>
        </div>

        {/* Video for the current round */}
        <div className="group-content">
          {audioSource && (
            <audio
              ref={audioRef}
              src={audioSource}
              autoPlay
              loop
              controls
              className="hidden-audio"
              data-testid="group-workout-audio"
            >
              Your browser does not support the audio element.
            </audio>
          )}
          <audio
            ref={countdownAudioRef}
            src={GROUP_WORKOUT_COUNTDOWN_AUDIO}
            preload="auto"
            className="hidden-audio"
            data-testid="group-workout-countdown-audio"
          >
            Your browser does not support the audio element.
          </audio>

          {workoutRound && workoutRound.hasQuote && (
            <div className='quote-wrapper'>
              <div className='quote-timer'>
                <CircularTimer
                  timerKey={workoutRound.id}
                  roundDuration={workoutRound.duration}
                  timeLeftInRound={roundTime}
                  timeLeftInSwitch={switchTimeLeft}
                  switchDuration={workoutRound.timeInSwitchRound}
                  isExplanation ={workoutRound.isExplanationRound}
                  isPaused={isPaused}
                  showSwitch={false}
                  size="large"
                />
              </div>

              <div className='quote-text-div'>
                <GroupQuote quote={workoutRound.quote} />
              </div>
            </div>
          )}
          {/* {workoutRound?.videoFile?.blobUrl ? (
            <div className="video-wrapper">
              <video
                ref={videoRef}
                id={`video-${workoutRound.id}`}
                src={workoutRound.videoFile.blobUrl}
                autoPlay
                loop
                muted
                playsInline
                className="fullscreen-video"
              />
            </div>
          ) : (
            <p>Loading video...</p>
          )} */}

          {/* {workoutRound.hasQuote && <GroupQuote />} */}
          {/* Show Intensity Page */}
          {workoutRound?.showIntensity && (
            <div className="intensity-template">
              {workoutRound?.videoFile?.blobUrl ? (
                <div className="video">
                  <div className="video-wrapper">
                    <video
                      ref={videoRef}
                      id={`video-${workoutRound.id}`}
                      src={workoutRound.videoFile.blobUrl}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="fullscreen-video"
                    />
                  </div>
                </div>
              ) : (
                <p className="video"></p>
                // <p className="video">Loading video...</p>
              )}

              <div className="timer">
                <CircularTimer
                  timerKey={workoutRound.id}
                  roundDuration={workoutRound.duration}
                  timeLeftInRound={roundTime}
                  timeLeftInSwitch={switchTimeLeft}
                  switchDuration={workoutRound.timeInSwitchRound}
                  isExplanation ={workoutRound.isExplanationRound}
                  isPaused={isPaused}
                  showSwitch={!!switchInfo}
                  size="mediumplus"
                  switchInfo={switchInfo ? {
                    currentSwitchRound: switchInfo.currentSwitch,
                    totalSwitches: switchInfo.totalSwitches,
                  } : undefined}
                />
              </div>
              <div className="intensity">
                <div className="intensity">
                  <GroupIntensity
                    serverTime={serverTime}
                    groupBags={groupBags}
                    groupWorkoutHits={currentLiveRoundHits}
                    exerciseSecond={getCurrentExerciseSecond()}
                  />
                </div>
              </div>
            </div>
          )}
          {/* Show Hit Challenge Page */}
          {workoutRound?.showHitScore && (
            <div className="group-hit-challenge-wrapper hit-grid-container">
              <div className="graph">
                <GroupHitChallenge
                  groupBags={groupBags}
                  groupWorkoutHits={currentLiveRoundHits}
                />
              </div>
              <div className="timer">
                <CircularTimer
                  timerKey={workoutRound.id}
                  size="medium"
                  roundDuration={workoutRound.duration}
                  timeLeftInRound={roundTime}
                  timeLeftInSwitch={switchTimeLeft}
                  switchDuration={workoutRound.timeInSwitchRound}
                  isExplanation ={workoutRound.isExplanationRound}
                  isPaused={isPaused}
                  showSwitch={!!switchInfo}
                  switchInfo={switchInfo ? {
                    currentSwitchRound: switchInfo.currentSwitch,
                    totalSwitches: switchInfo.totalSwitches,
                  } : undefined}
                />
              </div>
            </div>
          )}

          {/* Show Group Challenge Page */}
          {workoutRound?.showGroupChallenge && (
            <div className="group-group-challenge-wrapper">
              <div className="score">
                <GroupGroupChallenge
                  groupBags={groupBags}
                  groupWorkoutHits={currentLiveRoundHits}
                />
              </div>
              <div className="timer">
                <CircularTimer
                    timerKey={workoutRound.id}
                    size="medium"
                    roundDuration={workoutRound.duration}
                    timeLeftInRound={roundTime}
                    timeLeftInSwitch={switchTimeLeft}
                    switchDuration={workoutRound.timeInSwitchRound}
                    isExplanation ={workoutRound.isExplanationRound}
                    isPaused={isPaused}
                    showSwitch={!!switchInfo}
                    switchInfo={switchInfo ? {
                      currentSwitchRound: switchInfo.currentSwitch,
                      totalSwitches: switchInfo.totalSwitches,
                    } : undefined}
                  />
                </div>
            </div>
          )}
          {/* Show Single timer Page */}
          {
            workoutRound &&
            !workoutRound?.showHitScore &&
            !workoutRound?.showGroupChallenge &&
            !workoutRound?.hasQuote &&
            !workoutRound?.showIntensity &&
            (!workoutRound?.isSwitchRound || !switchInfo ? (
              <div className="fullscreen-timer">
                <div className="timer">
                  <CircularTimer
                    timerKey={workoutRound.id}
                    size="large"
                    roundDuration={workoutRound.duration}
                    timeLeftInRound={roundTime}
                    isPaused={isPaused}
                    isExplanation ={workoutRound.isExplanationRound}
                    showSwitch={false}
                  />
                </div>
              </div>
            ) : (
              <div className="fullscreen-timer">
                <div className="timer">
                  <CircularTimer
                      timerKey={workoutRound.id}
                      size="large"
                      roundDuration={workoutRound.duration}
                      timeLeftInRound={roundTime}
                      timeLeftInSwitch={switchTimeLeft}
                      switchDuration={workoutRound.timeInSwitchRound}
                      isExplanation ={workoutRound.isExplanationRound}
                      isPaused={isPaused}
                      showSwitch={true}
                      switchInfo={{
                        currentSwitchRound: switchInfo.currentSwitch,
                        totalSwitches: switchInfo.totalSwitches,
                      }}
                    />
                  </div>
                </div>
            ))
}
          {/* {workoutTrack && !workoutRound?.isSwitchRound ? (
            <div className="round-info">
              <h2>
                {workoutTrack.name}
                {workoutTrack.isPreWorkout && ' (Pre-Workout)'}
                {workoutTrack.isPostWorkout && ' (Post-Workout)'}
              </h2>
              <h3>Round: {workoutRound?.name}</h3>
              <p>Time Remaining: {roundTime}</p>
              <CircularTimer
                size="small"
                roundDuration={workoutRound.duration}
                timeLeftInRound={roundTime}
                isPaused={isPaused}
                showSwitch={false}
              />
              {workoutRound?.isSwitchRound && switchInfo && (
                <div className="switch-timer">
                  <h3>
                    Switch {currentSwitchIndex + 1} of {workoutRound.totalSwitchRounds}
                  </h3>
                  <p>Time Remaining in Switch: {switchTimeLeft}</p>
                  <CircularTimer
                    size="small"
                    roundDuration={workoutRound.duration}
                    timeLeftInRound={roundTime}
                    timeLeftInSwitch={switchTimeLeft}
                    switchDuration={workoutRound.timeInSwitchRound}
                    isPaused={isPaused}
                    showSwitch={true}
                    switchInfo={{
                      currentSwitchRound: switchInfo.currentSwitch,
                      totalSwitches: switchInfo.totalSwitches,
                    }}
                  />
                </div>
              )}
            </div>
          ) : (
            <p>Loading...</p>
          )} */}

          <HitSimulate />
        </div>

        <div className="group-controls">
          {showVolumeSlider && (
            <div
              className="volume-slider-container"
              style={{
                position: 'absolute',
                bottom: '100px',
                right: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                padding: '10px',
                borderRadius: '5px',
                width: '400px',
                zIndex: 1000,
              }}
            >
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                onChange={(e) => setVolume(Number(e.target.value))}
              />
            </div>
          )}
          <ControlsFooter
            bags={groupBags.filter((b) => b.isEnabled).length}
            openBagsPopup={() => openBags()}
            onVolumeClick={toggleVolumeSlider}
          >
            <Controls handlePause={customHandlePause} handlePrev={customHandlePrev} handleNext={customHandleNext} />
          </ControlsFooter>
        </div>
      </div>
    </Main>
  )
}

export default GroupWorkout
